/**
 * Runtime configuration. The ONLY module that reads import.meta.env.
 *
 * Only PUBLIC values may be exposed to the browser (VITE_ prefix):
 * the Supabase project URL and anon key are public by design — all data
 * protection is enforced by Row Level Security on the server.
 * Service-role keys, Resend keys and payment secrets must NEVER be added here.
 * The one other browser value is the map tile key, public by design and
 * restricted to approved domains in the provider account (CLAUDE.md §8).
 */

export type AppEnvironment = 'development' | 'staging' | 'production';

function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readBool(value: unknown, fallback: boolean): boolean {
  const v = readString(value).toLowerCase();
  if (v === 'true' || v === '1') return true;
  if (v === 'false' || v === '0') return false;
  return fallback;
}

function readEnvironment(value: unknown): AppEnvironment {
  const v = readString(value);
  return v === 'staging' || v === 'production' ? v : 'development';
}

export function isValidSupabaseUrl(url: string): boolean {
  try {
    const u = new URL(url);
    if (u.protocol === 'https:') return true;
    return u.protocol === 'http:' && (u.hostname === 'localhost' || u.hostname === '127.0.0.1');
  } catch {
    return false;
  }
}

/** Guards against accidentally shipping a service-role key to the browser. */
export function looksLikeServiceRoleKey(key: string): boolean {
  if (key.startsWith('sb_secret_')) return true;
  const parts = key.split('.');
  if (parts.length !== 3 || !parts[1]) return false;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as { role?: unknown };
    return payload.role === 'service_role';
  } catch {
    return false;
  }
}

const raw = import.meta.env;

/**
 * Build-time constant (not the runtime `env` object) so production bundles
 * drop the staging-only media entirely: Vite inlines VITE_APP_ENV and the
 * dead branch — and the assets it imports — are never emitted.
 */
export const STAGING_MEDIA_BUILD = import.meta.env.VITE_APP_ENV !== 'production';
const appEnv = readEnvironment(raw.VITE_APP_ENV);
const supabaseUrl = readString(raw.VITE_SUPABASE_URL);
const supabaseAnonKey = readString(raw.VITE_SUPABASE_ANON_KEY);

if (supabaseAnonKey && looksLikeServiceRoleKey(supabaseAnonKey)) {
  // Fail closed: never run the app with a privileged key in the bundle.
  throw new Error('VITE_SUPABASE_ANON_KEY contains a service-role key. Use the anon/publishable key only.');
}

/**
 * Staging review mode (owner decision, CLAUDE.md §5): stakeholder review
 * builds hide inline approval markers and show one global STAGING ribbon.
 * Never active in production; local development keeps inline markers.
 */
const reviewMode = appEnv !== 'production' && readBool(raw.VITE_REVIEW_MODE, appEnv === 'staging');

export const env = {
  appEnv,
  appUrl: readString(raw.VITE_APP_URL) || (typeof window !== 'undefined' ? window.location.origin : ''),
  supabaseUrl,
  supabaseAnonKey,
  isSupabaseConfigured: isValidSupabaseUrl(supabaseUrl) && supabaseAnonKey.length > 20,
  /** Shows draft-content / sample-data markers for stakeholder review. Off in production. */
  showContentStatus: appEnv !== 'production' && !reviewMode && readBool(raw.VITE_SHOW_CONTENT_STATUS, true),
  reviewMode,
  attributionEnabled: readBool(raw.VITE_ATTRIBUTION_ENABLED, true),
  /** Map tiles (MapTiler). Empty = the centers page is list-only. */
  mapTilesKey: readString(raw.VITE_MAPTILER_KEY),
  mapStyle: readString(raw.VITE_MAP_STYLE) || 'streets-v2',
  isProduction: appEnv === 'production',
} as const;
